import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useWorkspaceTerminology } from "@/domain/workspace/hooks/use-workspace-terminology";
import { useUnsafeCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import type { I18nNamespace } from "@/lib/i18n";

interface UseDomainTranslationOptions {
  workspaceId?: string;
}

/**
 * Convenience hook that wraps `useTranslation` and automatically injects
 * workspace terminology variables into every `t()` call.
 *
 * When used outside a WorkspaceProvider (e.g. public dashboard embed),
 * pass `{ workspaceId }` explicitly so terminology can still be resolved.
 *
 * Usage:
 * ```ts
 * const { t } = useDomainTranslation("climate-action");
 * t("createDialog.title"); // key: "{{climateAction}} erstellen" → "Maßnahme erstellen"
 * ```
 */
export function useDomainTranslation(namespace: I18nNamespace, options?: UseDomainTranslationOptions) {
  const { t: rawT, i18n, ready } = useTranslation(namespace);
  const workspaceContext = useUnsafeCurrentWorkspaceContext();
  const workspaceId = options?.workspaceId ?? workspaceContext?.currentWorkspace.id ?? "";
  const terminology = useWorkspaceTerminology(workspaceId);

  const terminologyVariableMap = useMemo(
    () => ({
      climateAction: terminology.climateAction.singular,
      climateActionPlural: terminology.climateAction.plural,
      project: terminology.project.singular,
      projectPlural: terminology.project.plural,
      task: terminology.task.singular,
      taskPlural: terminology.task.plural,
    }),
    [terminology],
  );

  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string => {
      return rawT(key as never, { ...terminologyVariableMap, ...options } as never) as unknown as string;
    },
    [rawT, terminologyVariableMap],
  );

  return { t, i18n, ready };
}
